/**
 * Store: Wishlist
 * ------------------------------------------------------------------------------
 * Global store for Swym Wishlist.
 * - https://api-docs.swym.it/v3/index.html#introduction-to-v3-0
 *
 * @namespace useWishlistStore
 */
import { defineStore } from 'pinia'

import Swym from '~/api/swym'

import { storageNamespace } from '~/helpers/storage-namespaces'

/**
 * Export a Wishlist global state.
 */
export const useWishlistStore = defineStore({
  id: 'wishlist',

  state: () => {
    return {
      wishlistData: false,
      wishlistReady: false,
    }
  },

  actions: {

    /**
     * Add a given product to a singular/default Wishlist.
     * @param {Object} product - Formatted product object.
     * @param {Object} variant - Formatted variant object.
     * @returns {Promise}
     */
    async addToWishlist(product, variant) {
      try {
        const response = await Swym().addToWishlist(product, variant)

        await this.handleWishlistUpdate()

        return response

      } catch (error) {
        return error
      }
    },

    /**
     * Set an array of wishlist products into store data.
     *
     * UPD: Replaced with single Wishlist.
     */
    async handleWishlistUpdate() {
      try {
        const response = await Swym().getWishlist()
        this.setWishlist(response)

        /**
         * Dispatch an event for other Sections and ES6 components
         * - section-methods will listen to this and run setWishlist, to
         *   keep the Wishlist Pinia states in sync
         * - ES6 components can also listen to this.
         */
        mint.EventBus.dispatch('mint:wishlist:updated', response)

      } catch (error) {
        mint.Log('error', 'Error updating Wishlist', error)
      }
    },

    /**
     * Remove a given product to a singular/default Wishlist.
     * @param {Object} product - Formatted product object.
     * @param {Object} variant - Formatted variant object.
     * @param {Object} lid - OPTIONAL: Wishlist ID. Used for compatibility
     * of a single Wishlist with existing/added not via the JS multiple lists.
     * @returns {Promise}
     */
    async removeFromWishlist(product, variant, lid = mint.services.swym.listId) {
      try {
        const response = await Swym().removeFromWishlist(product, variant, lid)

        this.handleWishlistUpdate()

        return response

      } catch (error) {
        return error
      }
    },

    /**
     * Trigger the 'Back in stock' modal.
     * @param {Object} product - Formatted product object.
     * @param {Object} variant - Formatted variant object.
     * @returns {Promise}
     */
    async openBackInStock(product, variant) {
      try {
        const response = await Swym().openBackInStock(product, variant)

        this.handleWishlistUpdate()

        return response

      } catch (error) {
        return error
      }
    },

    /**
     * Standalone method to update the wishlist in the global state
     * - This method is used in mint-sections.js to keep the Pinia
     *   state of each section is sync when the Wishlist is modified.
     * - Response is added to browser cache, so when new section mounts
     *   it can add the data to that sections Pinia without doing
     *   another API call.
     * @param {Object} response
     */
    setWishlist(response) {
      if (!response) {
        return
      }

      this.wishlistData = response
      this.wishlistReady = true
      sessionStorage.setItem(storageNamespace.pinia.wishlist, JSON.stringify(response))
    },

    /**
     * Send a back-in-stock alert to Swym.
     * @param {Object} product - Formatted product object.
     * @param {Object} variant - Formatted variant object.
     * @param {String} email - User email to which the notification should be sent.
     *
     * @returns {Promise}
     */
    async sendWatchlist(product, variant, email) {
      try {
        const response = await Swym().sendWatchlist(product, variant, email)

        return response
      } catch (error) {
        return error
      }
    },
  },
})
